import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Cookie, Database, Mail, Lock, XCircle } from 'lucide-react';
import Navbar from './Navbar';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const sections = [
    {
      icon: Database,
      title: 'Information We Collect',
      content: [
        'When you visit our website or use our contact forms, we may collect the following information:',
        '• Personal Information: Name, email address, phone number, and company information when voluntarily provided through our contact forms',
        '• Usage Data: Information about how you use our website, including pages visited, time spent, and referring websites',
        '• Technical Data: IP address, browser type, operating system, and device information',
        '• Communication Data: Records of your communications with us, including emails and support requests'
      ]
    },
    {
      icon: Eye,
      title: 'How We Use Your Information',
      content: [
        'We use the collected information for the following purposes:',
        '• To respond to your inquiries and provide customer support',
        '• To communicate with you about our services and updates',
        '• To improve our website and services based on user feedback and usage patterns',
        '• To ensure the security and proper functioning of our website',
        '• To comply with legal obligations and protect our rights'
      ]
    },
    {
      icon: Cookie,
      title: 'Cookies and Tracking Technologies',
      content: [
        'Our website may use cookies and similar tracking technologies to:',
        '• Remember your preferences and settings',
        '• Analyze website traffic and usage patterns',
        '• Improve website functionality and user experience',
        '• Provide relevant content and advertisements',
        '',
        'You can control cookie settings through your browser preferences. However, disabling cookies may affect website functionality.'
      ]
    },
    {
      icon: XCircle,
      title: 'We Do Not Sell Your Information',
      content: [
        'Granite State Cloud Solutions is committed to protecting your privacy and maintaining your trust. We want to be absolutely clear about our practices:',
        '• We DO NOT sell your personal information to third parties for any reason',
        '• We DO NOT broker, trade, or monetize your personal data',
        '• We DO NOT share your information with data aggregators or marketing companies',
        '• We DO NOT profit from your personal information in any way',
        '',
        'Your information is collected solely for the purpose of providing our services and communicating with you about your business needs. We respect your privacy and will never compromise it for financial gain.'
      ]
    },
    {
      icon: Shield,
      title: 'Information Sharing and Disclosure',
      content: [
        'We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:',
        '• With your explicit consent',
        '• With third-party service providers when necessary to deliver our services to you (such as cloud platforms, hosting providers, or technology partners)',
        '• To comply with legal obligations or respond to legal requests',
        '• To protect our rights, property, or safety, or that of our users',
        '• With trusted service providers who assist in operating our website (under strict confidentiality agreements)',
        '• In connection with a business transfer, merger, or acquisition',
        '',
        'When we share information with third parties to provide our services, we ensure they maintain appropriate security measures and use your information solely for the intended service delivery.'
      ]
    },
    {
      icon: Lock,
      title: 'Data Security',
      content: [
        'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:',
        '• Secure Socket Layer (SSL) encryption for data transmission',
        '• Regular security assessments and updates',
        '• Limited access to personal information on a need-to-know basis',
        '• Secure data storage and backup procedures',
        '',
        'However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.'
      ]
    },
    {
      icon: Mail,
      title: 'Your Rights and Choices',
      content: [
        'You have the following rights regarding your personal information:',
        '• Access: Request information about the personal data we hold about you',
        '• Correction: Request correction of inaccurate or incomplete personal data',
        '• Deletion: Request deletion of your personal data (subject to legal obligations)',
        '• Portability: Request a copy of your personal data in a structured format',
        '• Opt-out: Unsubscribe from marketing communications at any time',
        '',
        'To exercise these rights, please contact us using the information provided below.'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-50 to-white pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </button>
          
          <div className="text-center">
            <div className="flex items-center justify-center w-24 h-24 bg-primary-100 rounded-2xl mx-auto mb-6">
              <Shield className="h-12 w-12 text-primary-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-2xl shadow-sm">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              At Granite State Cloud Solutions ("we," "our," or "us"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using our website or services, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our website or services.
            </p>
          </div>
        </div>
      </section>

      {/* Policy Sections */}
      {sections.map((section, index) => {
        const IconComponent = section.icon;
        const sectionId = section.title === 'Cookies and Tracking Technologies' ? 'cookies-section' : undefined;
        return (
          <section 
            key={index} 
            id={sectionId}
            className={`py-16 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
          >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-xl">
                    <IconComponent className="h-8 w-8 text-primary-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {section.title}
                  </h2>
                  <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
                    {section.content.map((paragraph, pIndex) => (
                      <p key={pIndex} className={paragraph === '' ? 'mb-2' : ''}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Additional Information */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-2xl shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Third-Party Links and Services
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Our website may contain links to third-party websites or services that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services. We encourage you to review the privacy policy of every site you visit.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Children's Privacy
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Our services are not intended for individuals under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and you become aware that your child has provided us with personal information, please contact us immediately.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Changes to This Privacy Policy
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" above. You are advised to review this Privacy Policy periodically for any changes.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Contact Us
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 font-medium mb-2">Granite State Cloud Solutions</p>
              <p className="text-gray-700 mb-2">Email: contact@gscsnh.com</p>
              <p className="text-gray-700">Website: www.gscsnh.com</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;